import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { PageContainer } from '../../../common/components/PageContainer';
import { usePlansApi } from '../../../common/hooks/use-plans-api.hook';
import { useVendorPlan } from '../../../common/hooks/use-vendor-plan.hook';
import { usePlanUsage } from '../../../components/design-system/plan/usePlanUsage';
import { ACTIVE_ACCESS } from '../../../components/design-system/plan/access';
import { VendorPlan } from '../../superadmin/our-vendors/models/vendor.model';
import { PricedPlan, applyOffer, money, monthlyPrice, planHighlights, purchasable } from './plan-ladder';

/**
 * What an expired vendor sees instead of a page (docs/pricing-v2.md): the
 * trial or subscription ended, nothing was lost, and here is how to carry on.
 *
 * Rendered in the app shell, so the sidebar and the billing pages stay
 * reachable. The server is what enforces it — this only explains.
 */
export function TrialEndedWall() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const plansApi = usePlansApi();
  const vp = useVendorPlan();
  const access = vp.access ?? ACTIVE_ACCESS;
  const planSlug = vp.planSlug;
  const { meter } = usePlanUsage();
  const [plans, setPlans] = useState<VendorPlan[] | null>(null);

  useEffect(() => {
    let live = true;
    Promise.all([Promise.resolve(plansApi.getPlans()), Promise.resolve(plansApi.getMyPlan()).catch(() => null)])
      .then(([rows, mine]: any[]) => {
        if (!live) return;
        const priced = applyOffer(purchasable((rows as VendorPlan[]) || []), mine?.offer) as PricedPlan[];
        // With an offer, the wall shows the vendor's own prices, plus Enterprise for a bigger need.
        const shown = priced.some((p) => p.offered) ? priced.filter((p) => p.offered || p.pricingType === 'custom') : priced;
        setPlans(shown.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
      })
      .catch(() => live && setPlans([]));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscriptionEnded = access.reason === 'subscription_ended';

  // What the trial built — the reason to keep going, in the vendor's own numbers.
  const built = useMemo(() => {
    const rows: { key: string; value: number }[] = [];
    for (const key of ['maxProducts', 'maxRoomAnalysesPerMonth', 'maxLeads'] as const) {
      const pair = meter(key);
      if (pair && pair.current > 0) rows.push({ key, value: pair.current });
    }
    return rows;
  }, [meter]);

  const choose = (plan: VendorPlan) =>
    navigate(`/home/billing/upgrade?plan=${encodeURIComponent(plan.slug)}`);

  return (
    <PageContainer breadcrumbs={[{ label: t('PRICING.WALL.CRUMB') }]}>
      <div className="mx-auto flex max-w-[1040px] flex-col items-center py-6 md:py-12" data-testid="trial-ended-wall" data-plan={planSlug}>
        <span className="mb-4 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium text-muted-foreground">
          <span className="size-1.5 rounded-full bg-destructive" aria-hidden />
          {subscriptionEnded ? t('PRICING.WALL.BADGE_SUB') : t('PRICING.WALL.BADGE_TRIAL')}
        </span>
        <h1 className="font-heading text-center text-2xl font-medium tracking-tight md:text-3xl">
          {subscriptionEnded ? t('PRICING.WALL.TITLE_SUB') : t('PRICING.WALL.TITLE_TRIAL')}
        </h1>
        <p className="mt-3 max-w-[560px] text-center text-sm leading-relaxed text-muted-foreground">
          {t('PRICING.WALL.BODY')}
        </p>

        {built.length > 0 && (
          <div
            className="mt-8 grid w-full divide-x rounded-xl border bg-card"
            style={{ gridTemplateColumns: `repeat(${built.length}, minmax(0, 1fr))`, maxWidth: built.length * 190 }}
            data-testid="trial-built"
          >
            {built.map((b) => (
              <div key={b.key} className="px-4 py-3 text-center">
                <p className="font-heading text-2xl font-medium tabular-nums">{b.value.toLocaleString()}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{t(`PRICING.WALL.BUILT.${b.key}`)}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 w-full">
          {plans === null ? (
            <div className="flex justify-center py-10">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div
              className="grid grid-cols-1 gap-4 md:grid-cols-[repeat(var(--cols),minmax(0,1fr))]"
              style={{ '--cols': Math.max(1, Math.min(plans.length, 3)) } as React.CSSProperties}
            >
              {plans.map((plan, i) => {
                const custom = plan.pricingType === 'custom';
                const price = monthlyPrice(plan, 'month');
                const recommended = !custom && i === plans.filter((p) => p.pricingType !== 'custom').length - 1;
                return (
                  <div
                    key={plan._id}
                    data-testid={`wall-plan-${plan.slug}`}
                    className={cn('flex flex-col rounded-xl border bg-card p-5', recommended && 'border-foreground')}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-base font-medium">{plan.name}</p>
                      {(plan as PricedPlan).offered ? (
                        <span className="rounded-full border border-foreground px-2 py-0.5 text-[11px] font-medium">{t('PRICING.OFFER.YOUR_PRICE')}</span>
                      ) : recommended && (
                        <span className="rounded-full border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                          {t('PRICING.UPGRADE.POPULAR')}
                        </span>
                      )}
                    </div>
                    {plan.tagline && <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{plan.tagline}</p>}
                    <div className="mt-4 flex items-baseline gap-1.5">
                      {custom && <span className="text-sm text-muted-foreground">{t('PRICING.UPGRADE.FROM')}</span>}
                      <span className="font-heading text-2xl font-medium tracking-tight">
                        {price ? money(price, plan.currency) : t('PLANS.PRICE_CUSTOM', 'Custom')}
                      </span>
                      {price && <span className="text-sm text-muted-foreground">{t('PRICING.UPGRADE.PER_MONTH')}</span>}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {custom ? t('PRICING.UPGRADE.MIN_TERM') : t('PRICING.UPGRADE.MONTHLY_OR_YEARLY')}
                    </p>
                    <ul className="mt-4 flex flex-1 flex-col gap-2">
                      {planHighlights(plan, t).slice(0, 5).map((h) => (
                        <li key={h} className="flex items-start gap-2 text-sm">
                          <Check className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                    <Button variant="outline" className="mt-5 w-full" data-testid={`wall-choose-${plan.slug}`} onClick={() => choose(plan)}>
                      {custom ? t('PRICING.UPGRADE.TALK_TO_SALES') : t('PRICING.WALL.CHOOSE', { plan: plan.name })}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
          <button type="button" className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline" onClick={() => navigate('/home/billing/upgrade')}>
            {t('PRICING.WALL.COMPARE')}
          </button>
          <button type="button" className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline" onClick={() => navigate('/home/billing/plan')}>
            {t('PRICING.WALL.BILLING')}
          </button>
        </div>
        <p className="mt-6 max-w-[520px] text-center text-xs leading-relaxed text-muted-foreground">{t('PRICING.WALL.KEPT')}</p>
      </div>
    </PageContainer>
  );
}
